// Payments.jsx

import Header from "../Components/Header";
import "./Payments.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE, FILE_BASE } from "../config";

const Payments = () => {
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);

      const token = sessionStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE}/payment/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPayments(response.data);
    } catch (error) {
      console.log("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (item) => {
    navigate(`/payment-details/${item.id}`, {
      state: item,
    });
  };

  // TOTAL REVENUE
  const totalRevenue = useMemo(() => {
    return payments
      .filter((x) => x.status?.toLowerCase() === "success")
      .reduce((sum, item) => sum + Number(item.amount), 0);
  }, [payments]);

  // PENDING PAYMENTS
  const pendingPayments = useMemo(() => {
    return payments
      .filter((x) => x.status?.toLowerCase() === "pending")
      .reduce((sum, item) => sum + Number(item.amount), 0);
  }, [payments]);

  // REFUNDS
  const refunds = useMemo(() => {
    return payments
      .filter((x) => x.status?.toLowerCase() === "refunded")
      .reduce((sum, item) => sum + Number(item.amount), 0);
  }, [payments]);

  return (
    <div className="payments-page">
      <Header />

      {/* Heading */}
      <div className="payments-head">
        <h2>Payments</h2>
        <p>Track all payment transactions</p>
      </div>

      {/* Cards */}
      <div className="payments-cards">
        <div className="payments-card">
          <div className="payments-icon green">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#fff"
            >
              <path d="M441-120v-86q-53-12-91.5-46T293-348l74-30q15 48 44.5 73t77.5 25q41 0 69.5-18.5T587-356q0-35-22-55.5T463-458q-86-27-118-64.5T313-614q0-65 42-101t86-41v-84h80v84q50 8 82.5 36.5T651-650l-74 32q-12-32-34-48t-60-16q-44 0-67 19.5T393-614q0 33 30 52t104 40q69 20 104.5 63.5T667-358q0 71-42 108t-104 46v84h-80Z" />
            </svg>
          </div>

          <p>Total Revenue</p>

          <h3>
            ₹
            {totalRevenue.toLocaleString("en-IN")}
          </h3>
        </div>

        <div className="payments-card">
          <div className="payments-icon yellow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#fff"
            >
              <path d="M324-111.5Q251-143 197-197t-85.5-127Q80-397 80-480t31.5-156Q143-709 197-763t127-85.5Q397-880 480-880t156 31.5Q709-817 763-763t85.5 127Q880-563 880-480t-31.5 156Q817-251 763-197t-127 85.5Q563-80 480-80t-156-31.5ZM707-253q93-93 93-227 0-64-24-123t-69-104L480-480v-320q-134 0-227 93t-93 227q0 134 93 227t227 93q134 0 227-93Z" />
            </svg>
          </div>

          <p>Pending Payments</p>

          <h3>
            ₹
            {pendingPayments.toLocaleString("en-IN")}
          </h3>
        </div>

        <div className="payments-card">
          <div className="payments-icon pink">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#fff"
            >
              <path d="M482-160q-134 0-228-93t-94-227v-7l-64 64-56-56 160-160 160 160-56 56-64-64v7q0 100 70.5 170T482-240q26 0 51-6t49-18l60 60q-38 22-78 33t-82 11Zm278-161L600-481l56-56 64 64v-7q0-100-70.5-170T478-720q-26 0-51 6t-49 18l-60-60q38-22 78-33t82-11q134 0 228 93t94 227v7l64-64 56 56-160 160Z" />
            </svg>
          </div>

          <p>Refunds</p>

          <h3>
            ₹
            {refunds.toLocaleString("en-IN")}
          </h3>
        </div>
      </div>

      {/* Table */}
      <div className="payments-table-box">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Payment ID</th>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Payment Method</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  Loading payments...
                </td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No payments found
                </td>
              </tr>
            ) : (
              payments.map((item) => (
                <tr key={item.id}>
                  <td
                    className="bold"
                    onClick={() => openDetails(item)}
                  >
                    {item.paymentNumber}
                  </td>

                  <td
                    className="light"
                    onClick={() => openDetails(item)}
                  >
                    {item.orderNumber}
                  </td>

                  <td onClick={() => openDetails(item)}>
                    {item.customerName}
                  </td>

                  <td onClick={() => openDetails(item)}>
                    <span className="method-badge">
                      {item.paymentMethod}
                    </span>
                  </td>

                  <td
                    className="bold"
                    onClick={() => openDetails(item)}
                  >
                    ₹
                    {Number(item.amount).toLocaleString("en-IN")}
                  </td>

                  <td onClick={() => openDetails(item)}>
                    <span
                      className={`status-badge ${item.status?.toLowerCase()}`}
                    >
                      {item.status}
                    </span>
                  </td>

                  <td onClick={() => openDetails(item)}>
                    <span className="view-eye">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                        fill="#2854C5"
                      >
                        <path d="M607.5-372.5Q660-425 660-500t-52.5-127.5Q555-680 480-680t-127.5 52.5Q300-575 300-500t52.5 127.5Q405-320 480-320t127.5-52.5Zm-204-51Q372-455 372-500t31.5-76.5Q435-608 480-608t76.5 31.5Q588-545 588-500t-31.5 76.5Q525-392 480-392t-76.5-31.5ZM214-281.5Q94-363 40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200q-146 0-266-81.5ZM480-500Zm207.5 160.5Q782-399 832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280q113 0 207.5-59.5Z" />
                      </svg>
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Payments;